import type { Difficulty, GenContext, Question } from '@/core/types';
import { choice, fmt, forSkill, type QDraft } from '@/engine/questions/build';
import type { Rng } from '@/core/util/rng';

export type Maker = (ctx: GenContext) => Question;
export type LevelTable = Partial<Record<Difficulty, Maker | Maker[]>>;

/**
 * Dispatch to the makers registered for the requested difficulty. Each
 * difficulty owns structurally different tasks — not relabelled ones.
 */
export function byLevel(ctx: GenContext, table: LevelTable): Question {
  let d = ctx.difficulty;
  // fall back to nearest defined level
  for (let off = 0; off < 7; off++) {
    const down = table[(d - off) as Difficulty];
    const up = table[(d + off) as Difficulty];
    const hit = down ?? up;
    if (hit) {
      const makers = Array.isArray(hit) ? hit : [hit];
      return ctx.rng.pick(makers)(ctx);
    }
  }
  throw new Error('byLevel: no makers');
}

/** Bound question factory for a maths skill. */
export function mq(skillId: string) {
  const make = forSkill(skillId, 'math');
  return (ctx: GenContext, d: Omit<QDraft, 'difficulty'> & { difficulty?: Difficulty }) =>
    make(ctx, { difficulty: d.difficulty ?? ctx.difficulty, ...d } as QDraft);
}

/** Numeric multiple-choice with misconception-tagged distractors. */
export function numChoice(rng: Rng, correct: number, wrong: (number | [number, string])[], unit = '') {
  const u = unit ? ` ${unit}` : '';
  return choice(
    rng,
    `${fmt(correct)}${u}`,
    wrong.map((w) => (typeof w === 'number' ? `${fmt(w)}${u}` : ([`${fmt(w[0])}${u}`, w[1]] as [string, string]))),
  );
}

/** Spread of plausible numeric distractors around a value. */
export function nearby(rng: Rng, v: number, n = 3, step = 1): number[] {
  const out = new Set<number>();
  let guard = 0;
  while (out.size < n && guard++ < 50) {
    const delta = rng.pick([-3, -2, -1, 1, 2, 3]) * step;
    const c = v + delta;
    if (c !== v) out.add(Math.round(c * 1e6) / 1e6);
  }
  return [...out];
}

export const nonZero = (rng: Rng, lo: number, hi: number) => {
  let v = 0;
  while (v === 0) v = rng.int(lo, hi);
  return v;
};

export const thousands = (n: number) => n.toLocaleString('en-US').replace(/,/g, ' ');

export const roundTo = (v: number, dp: number) => Math.round(v * 10 ** dp) / 10 ** dp;
